import { useId, useState, type InputHTMLAttributes } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { useServiceAreaCheck } from "./useServiceAreaCheck";
import { ServiceAreaResult } from "./ServiceAreaResult";

export interface DeliveryAddressInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  label?: string;
  initialValue?: string;
  debounceMs?: number;
  onAddressChange?: (address: string) => void;
}

// Customer-facing address field with inline service-area feedback. Used on
// the home hero ([1c-public-catalog-ui.md] FR-2) and during checkout
// ([02-reservations-and-checkout/2c-cart-and-checkout-ui.md]). The result
// panel below the field renders ✓ / ✗ / loading per the spec sketch.
export function DeliveryAddressInput({
  label = "Delivery address",
  initialValue = "",
  debounceMs,
  onAddressChange,
  className,
  ...inputProps
}: DeliveryAddressInputProps): JSX.Element {
  const inputId = useId();
  const [value, setValue] = useState(initialValue);
  const { status, data, error, flush } = useServiceAreaCheck(value, debounceMs ?? 300);

  return (
    <div className={className}>
      <Label htmlFor={inputId} className="mb-1 block">
        {label}
      </Label>
      <div className="flex gap-2">
        <Input
          id={inputId}
          type="text"
          autoComplete="street-address"
          placeholder="123 Main St, City, State"
          value={value}
          onChange={(event) => {
            const next = event.target.value;
            setValue(next);
            onAddressChange?.(next);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              flush();
            }
          }}
          {...inputProps}
        />
        <Button type="button" onClick={flush} className="shrink-0 whitespace-nowrap">
          Check availability
        </Button>
      </div>
      <ServiceAreaResult status={status} data={data} error={error} />
    </div>
  );
}
